import { Component, inject, input, signal } from "@angular/core";
import { MatPaginatorModule, PageEvent } from "@angular/material/paginator";
import { Sort } from "@angular/material/sort";
import { Router } from "@angular/router";
import { catchError, Observable, of, Subscription } from "rxjs";
import { CurrentUserService } from "../../shared/CurrentUser.service";
import { GridDisplaySettings } from "../../shared/GridDisplaySettings";
import { IFilterCategoryDef, IFilterDefWithDateRange, ISelectedFilterDef, ISelectedFilterDefWithDateRange, SelectOption } from "../../shared/interfaces/IFilterDef";
import { PagedResults } from "../../shared/interfaces/PagedResults";
import { LoadingService } from "../../shared/services/loading-overlay/loading-overlay.component";
import { PaginatorService } from '../../shared/services/paginator.service';
import { QuickLinksService } from "../../shared/services/quick-links.service";
import { SearchType } from "../../shared/services/search-storage.service";
import { TypeaheadItem } from "../typeahead/typeahead.component";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { FilterCardComponent } from "../filter-card/filter-card.component";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

export interface GenericSearchPaginationSettings {
  IsDescending: boolean,
  PageIndex: number,
  SortColumn: string,
  ItemsPerPage: number,
}

@Component({
  selector: 'generic-search-page',
  templateUrl: 'generic-search-page.component.html',
  standalone: true,
  imports: [CommonModule, FilterCardComponent, MatIconModule, MatButtonModule, MatPaginatorModule, MatProgressSpinnerModule],
  providers: [LoadingService, QuickLinksService, CurrentUserService, BrowserAnimationsModule]
})
export class GenericSearchPageComponent<TRow, TSearchCriteria extends GridDisplaySettings> {
  private paginatorService = inject(PaginatorService);
  private router = inject(Router);

  public data = signal<TRow[]>([]);
  public PageTitle = input.required<string>();
  public filters = input<IFilterCategoryDef<TSearchCriteria>[]>([]);
  public selectedFilters = signal<ISelectedFilterDef<TSearchCriteria>[]>([]);

  public SearchForRows = input<(searchCriteria: TSearchCriteria) => Observable<PagedResults<TRow>>>(null);
  public EditRow = input<(row: TRow) => Observable<boolean>>(null);
  public CreateRow = input<() => Observable<boolean>>(null);
  public SearchHistoryType = input<SearchType>(null);

  private sortColumn: string = "";
  private navigationExtras = this.router.getCurrentNavigation().extras.state;
  private searchSubscription: Subscription = null;
  private itemsPerPage = this.paginatorService.getItemsPerPageDefault();
  private isSortDescending: boolean = false;

  protected pageIndex = signal(0);
  protected totalCount = signal(0);
  protected isLoading = signal(false);
  protected CollapseSearch = signal(false);
  protected pageSizeOptions = this.paginatorService.getPageSizeOptions();

  public InitializeFilters() {
    if (!this.navigationExtras) return;

    for (const quicklinkProperty in this.navigationExtras) {
      this.filters().forEach((filterCategory) => {
        filterCategory.filters.forEach((filter) => {
          if (filter.searchParameterProperty && quicklinkProperty == filter.searchParameterProperty.toString()) {
            const navParam = this.navigationExtras[quicklinkProperty]
            switch (filter.inputType) {
              case "text":
                this.selectedFilters.update((current) => [...current, { filter: filter, searchValue: navParam }]);
                break;
              case "autocomplete":
                const autocomplete = (filter.options as Array<TypeaheadItem<any>>).filter((option) => navParam.includes(option.Item));
                this.selectedFilters.update((current) => [...current, { filter: filter, searchValue: autocomplete.map((option) => option.Item) }]);
                break;
              case "selectSingle":
                const selectSingle = (filter.options as Array<SelectOption<any>>).find(option => option.value == navParam);
                this.selectedFilters.update((current) => [...current, { filter: filter, searchValue: selectSingle ? selectSingle.value : null }]);
                break;
              case "selectMulti":
                const selectedOptions = (filter.options as Array<SelectOption<any>>).filter((option) => navParam.includes(option.value));
                this.selectedFilters.update((current) => [...current, { filter: filter, searchValue: selectedOptions.map((option) => option.value) }]);
                break;
              case "none":
                this.selectedFilters.update((current) => [...current, { filter: filter, searchValue: filter.defaultValue }]);
                break;
            }
          } else {
            const dateRangeFilter = filter as IFilterDefWithDateRange<TSearchCriteria>;
            const filterInUse = this.selectedFilters().some((f) => f.filter == filter);
            const propertyMatchesDateParameter = (dateRangeFilter.EndDateSearchParameterProperty && quicklinkProperty == dateRangeFilter.EndDateSearchParameterProperty.toString()) || (dateRangeFilter.StartDateSearchParameterProperty && quicklinkProperty == dateRangeFilter.StartDateSearchParameterProperty.toString());

            if (!filterInUse && propertyMatchesDateParameter) {
              this.selectedFilters.update((current) => [...current, {
                filter: filter,
                EndDateSearchValue: this.navigationExtras[dateRangeFilter.EndDateSearchParameterProperty as string],
                StartDateSearchValue: this.navigationExtras[dateRangeFilter.StartDateSearchParameterProperty as string]
              } as ISelectedFilterDefWithDateRange<TSearchCriteria>])
            }
          }
        });
      })
    }

    this.updateItems();
  }

  public GetCurrentPageSettings = (): GenericSearchPaginationSettings => ({
    IsDescending: this.isSortDescending,
    PageIndex: this.pageIndex(),
    SortColumn: this.sortColumn,
    ItemsPerPage: this.itemsPerPage
  });

  private updateItems() {
    this.isLoading.set(true);

    this.sortColumn = this.sortColumn.toLowerCase();
    this.data.set([]);

    if (this.searchSubscription != null && this.searchSubscription.closed == false) {
      this.searchSubscription.unsubscribe();
    }

    this.searchSubscription = this.SearchForRows()(this.GetSearchParameters(this.GetCurrentPageSettings()))
      .pipe(catchError(() => of([])))
      .subscribe((ps: PagedResults<TRow>) => {
        if (ps) {
          this.data.set(ps.Rows);
          this.totalCount.set(ps.TotalCount);
        }
        this.isLoading.set(false);
      });
  }

  protected ToggleSearchSection() {
    this.CollapseSearch.set(!this.CollapseSearch());
  }

  protected OnSearchButtonClick() {
    this.pageIndex.set(0);
    this.updateItems();
  }

  public edit(row: TRow) {
    this.EditRow()(row).subscribe((shouldRefresh) => {
      if (shouldRefresh)
        this.updateItems();
    });
  }

  public GetSearchParameters(page: GenericSearchPaginationSettings): TSearchCriteria {
    const parameters: TSearchCriteria = <any>{
      IsDescending: page.IsDescending,
      ItemsPerPage: page.ItemsPerPage,
      OrderBy: page.SortColumn,
      Page: page.PageIndex,
    };

    this.selectedFilters().forEach((filter) => {
      if (filter.filter.searchParameterProperty) {
        (<any>parameters)[filter.filter.searchParameterProperty] = filter.searchValue;
      } else {
        const selectedDateRangeFilter = filter as ISelectedFilterDefWithDateRange<TSearchCriteria>;
        const dateRangeFilter = selectedDateRangeFilter.filter as IFilterDefWithDateRange<TSearchCriteria>;
        (<any>parameters)[dateRangeFilter.StartDateSearchParameterProperty] = selectedDateRangeFilter.StartDateSearchValue;
        (<any>parameters)[dateRangeFilter.EndDateSearchParameterProperty] = selectedDateRangeFilter.EndDateSearchValue;
      }
    });

    return parameters;
  }

  protected onPageChanged(e: PageEvent) {
    this.itemsPerPage = e.pageSize;
    this.pageIndex.set(e.pageIndex);
    this.updateItems();
  }

  public onSortChange(sorter: Sort) {
    this.sortColumn = sorter.active;
    if (sorter.direction === 'asc')
      this.isSortDescending = false;
    else 
      this.isSortDescending = true;
    this.updateItems();
  }
}
